/**
 * speakerPalette — single source of truth for the "studio-backdrop"
 * tile colors used on the home screen and every other speaker grid.
 *
 * Two-tier lookup:
 *   1. `SPEAKER_BACKDROP_MAP` — hand-tuned per-speaker matches (generated
 *      by `backend/scripts/match_speaker_backdrops.py`). Picks the most
 *      flattering studio backdrop for each portrait based on face
 *      luminance + garment tone.
 *   2. Round-robin fallback across the 225-color interleaved palette
 *      when a speaker has no personal match (e.g. brand-new speaker,
 *      or no cutout portrait analysed yet).
 *
 * Palette (Feb-04 v3): 13 families totalling 225 studio hues covering
 * navy / forest / plum / teal / charcoal / red / rust / gold / pink /
 * salmon / tan / brown / white — full spectrum diversity per user's
 * "use all colors" ask.
 */
import { SPEAKER_BACKDROP_MAP } from './speakerBackdropMap';

/** Feb-04 v3 — user opened the constraint: "you can use all colors...
 *  make a ton of new colors so you see a lot more diversity." Expanded
 *  from 5 dark-warm families to 13 families covering the full studio-
 *  photography spectrum: warm-reds, orange/rust, gold/yellow, pinks,
 *  salmons/tans, browns, whites/ivories, plus the original navy /
 *  forest / plum / teal / charcoal / warm-neutrals. Total ≥ 200 hues,
 *  interleaved so consecutive tiles are always from different families. */

const WARM_NAVY = [
  '#1c2542', '#232f4e', '#2a3652', '#173350', '#1a1f45',
  '#152547', '#0f1729', '#2c3a5c', '#1c3054', '#1a2740',
  '#2e4463', '#172c50', '#3a4a70', '#1c2c53', '#253559',
  '#10233d', '#253a68', '#14274a', '#263e6b', '#1e335a',
  '#182238', '#22314e', '#172d54', '#1a304f', '#2b3d62',
];

const FOREST = [
  '#1f3325', '#2b3d2b', '#182b1e', '#2f4034', '#153525',
  '#1e2f21', '#243a2c', '#22422a', '#1a2b1f', '#294028',
  '#1d3623', '#2e4432', '#14261a', '#253a2a', '#1a352a',
  '#21332a', '#2f4237', '#1b3521', '#26362a', '#22392e',
  '#334c35', '#3a5138', '#28402b', '#2b4632',
];

const PLUM = [
  '#2e1f36', '#301c30', '#2c1f42', '#3b2340', '#34213a',
  '#28182e', '#241827', '#3a1a3e', '#2a1637', '#3b1f42',
  '#422348', '#26173a', '#351f3c', '#331d38', '#2b1832',
  '#3b2545', '#241a30', '#3f2545', '#4a2d54', '#442850',
];

const DEEP_TEAL = [
  '#12363f', '#193a45', '#0f394d', '#0e2f3b', '#122d3e',
  '#14424e', '#103a48', '#163c4a', '#124253', '#124a5e',
  '#16303a', '#103f4c', '#163445', '#0d3542', '#133f4e',
  '#1c4a5a', '#215061',
];

const CHARCOAL_BLUE = [
  '#161d2c', '#1a2130', '#20293a', '#1c2432', '#22283a',
  '#1e2634', '#23283b', '#1d2530', '#262c3e', '#1a1f2b',
  '#23293d', '#1d2836',
];

// ── NEW FAMILIES (Feb-04 v3) ──

const WARM_RED = [
  '#5c1f27', '#4b1a1d', '#6c2732', '#5b2a1d', '#6b1f2a',
  '#5a2b1d', '#7a1e2b', '#8a2432', '#732330', '#4a1720',
  '#65212a', '#7d2635', '#5f1b25', '#832836', '#4e1b1e',
  '#6d1a24', '#5b1a1f', '#943040', '#722130', '#552024',
];

const ORANGE_RUST = [
  '#7a3d1a', '#8b4a1e', '#a15127', '#6d3418', '#824417',
  '#9c5836', '#a45a24', '#6a3a1a', '#88461f', '#7c3f1c',
  '#a25a2d', '#734020', '#93502a', '#a86233', '#5f321a',
  '#874a26', '#96522c',
];

const GOLD_YELLOW = [
  '#9a7b24', '#7d6119', '#b0862a', '#a8801b', '#b58a2f',
  '#8c6a20', '#a4842d', '#8f7025', '#c19a37', '#75601c',
  '#b6902d', '#987527', '#a8842b', '#89701e', '#c0a03d',
  '#af8628', '#7d6720',
];

const PINK_ROSE = [
  '#a05470', '#8f4863', '#b76a83', '#a25e78', '#c17593',
  '#984d68', '#a55c76', '#b16b7f', '#8a4460', '#a4506b',
  '#b56a80', '#93475f', '#c07a92', '#8d4257', '#a2506a',
  '#9e4b64',
];

const SALMON_CORAL = [
  '#b76451', '#a55943', '#c67665', '#9d503d', '#b26350',
  '#c26e57', '#a75b48', '#bd6b58', '#b76a52', '#a75742',
  '#c47762', '#9b4b3a', '#b06556', '#c8735c', '#a2513f',
];

const TAN_BEIGE = [
  '#8a6d3b', '#a0814a', '#6d4f2a', '#907450', '#7d5d38',
  '#98805c', '#8b6d48', '#7a5e3a', '#a7885c', '#6a4f30',
  '#93744e', '#a58562', '#8c704a', '#6d5230', '#957a58',
];

const BROWN = [
  '#4a2f10', '#5a3a1a', '#3d2b1f', '#5f4423', '#3a2a1c',
  '#4b2f18', '#3a2718', '#5e3b1f', '#3f2917', '#4a3220',
  '#38251b', '#523825', '#472b1a', '#5c3d24', '#3d2818',
];

const WHITE_STUDIO = [
  '#f3ecdf', '#f4efe4', '#f7f5f0', '#faf6ee', '#eee8d9',
  '#fbf7e8', '#ede4d1', '#f6ecc9', '#f0e9d7', '#efe6cf',
  '#f5edd5', '#e8dfc9',
];

/** Interleaved palette: cycle every family so consecutive positions
 *  are ALWAYS from different families — no two adjacent tiles can
 *  look similar. Longer families keep contributing colors even after
 *  shorter ones exhaust. */
const _interleaveFamilies = () => {
  const families = [
    WARM_NAVY, FOREST, PLUM, DEEP_TEAL, CHARCOAL_BLUE,
    WARM_RED, ORANGE_RUST, GOLD_YELLOW, PINK_ROSE, SALMON_CORAL,
    TAN_BEIGE, BROWN, WHITE_STUDIO,
  ];
  const maxLen = Math.max(...families.map((f) => f.length));
  const out = [];
  for (let i = 0; i < maxLen; i += 1) {
    for (const f of families) {
      if (i < f.length) out.push(f[i]);
    }
  }
  return out;
};

export const SCENE_PALETTE = _interleaveFamilies();

const NOISE_SVG = (
  'data:image/svg+xml;utf8,' +
  "<svg xmlns='http://www.w3.org/2000/svg' width='240' height='240'>" +
  "<filter id='n'>" +
  "<feTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='2' stitchTiles='stitch'/>" +
  "<feColorMatrix values='0 0 0 0 1  0 0 0 0 1  0 0 0 0 1  0 0 0 0.55 0'/>" +
  '</filter>' +
  "<rect width='100%' height='100%' filter='url(%23n)'/>" +
  '</svg>'
);

/** Photo-real studio-backdrop CSS for a given base color:
 *  noise grain → highlight (top-left) → vignette (bottom-right) → base. */
export const sceneCss = (baseHex) => (
  `url("${NOISE_SVG}") 0 0 / 240px 240px, ` +
  'radial-gradient(circle at 20% 15%, rgba(255,255,255,0.14) 0%, rgba(255,255,255,0) 40%), ' +
  'radial-gradient(circle at 80% 100%, rgba(0,0,0,0.55) 0%, rgba(0,0,0,0) 55%), ' +
  baseHex
);

export const PALETTE_PRIMED_EVENT = 'vaad-palette-primed';

let nameToIndex = new Map();
// Feb-2026 (user request): "no two of the same colors next to or even
// one away from another." After the raw name → position map is primed
// we run a greedy collision-avoidance pass that guarantees for every
// slot `i`, `colorAt(i)` differs from `colorAt(i-1)` AND `colorAt(i-2)`.
// The final chosen color per name lives here so every SpeakerCard reads
// the same value.
let nameToColor = new Map();
// User-supplied overrides (persisted to localStorage) — a manual choice
// from the "positioning settings" backdrop-color picker always wins
// over the greedy assignment.
const USER_OVERRIDES_KEY = 'vaad_backdrop_overrides_v1';
const API_BASE = (typeof process !== 'undefined' && process.env && process.env.REACT_APP_BACKEND_URL) || '';

let userOverrides = (() => {
  try {
    const raw = typeof localStorage !== 'undefined' && localStorage.getItem(USER_OVERRIDES_KEY);
    const p = raw ? JSON.parse(raw) : null;
    return p && typeof p === 'object' ? p : {};
  } catch { return {}; }
})();

// Feb-2026 permanent rule (user): backdrop color changes MUST persist.
// The localStorage store already survived reloads on the same device, but
// picks on one device didn't reach another. On boot, pull the persisted
// overrides from the backend, merge into whatever's in localStorage
// (backend wins for anything set on both), then re-prime the palette.
let _backdropsHydrated = false;
async function _hydrateBackdropsFromServer() {
  if (_backdropsHydrated) return;
  _backdropsHydrated = true;
  try {
    const res = await fetch(`${API_BASE}/api/portrait/backdrops`);
    if (!res.ok) return;
    const data = await res.json();
    const serverMap = (data && data.overrides) || {};
    if (Object.keys(serverMap).length === 0) return;
    userOverrides = { ...userOverrides, ...serverMap };
    try { localStorage.setItem(USER_OVERRIDES_KEY, JSON.stringify(userOverrides)); } catch { /* quota */ }
    // Re-apply overrides to any already-primed name → color mapping.
    Object.entries(serverMap).forEach(([n, hex]) => {
      if (nameToColor.size) nameToColor.set(n, hex);
    });
    if (typeof window !== 'undefined') {
      window.dispatchEvent(new Event(PALETTE_PRIMED_EVENT));
    }
  } catch (_) { /* ignore */ }
}
if (typeof window !== 'undefined') {
  // Fire once shortly after import — allows unit tests / SSR to skip.
  setTimeout(_hydrateBackdropsFromServer, 100);
}

/** Called once after fetching the canonical speaker list so every
 *  SpeakerCard can look up its tile color by ordered position. Safe
 *  to call again — a later prime just overrides earlier data. */
export function primePaletteOrder(speakerNames) {
  if (!Array.isArray(speakerNames)) return;
  nameToIndex = new Map(speakerNames.map((n, i) => [n, i]));

  // Greedy pass: assign each name a color that doesn't collide with
  // the previous 2 assigned colors. First candidate is the hand-picked
  // SPEAKER_BACKDROP_MAP entry (falls back to round-robin index). If
  // that duplicates -1 or -2, walk forward through the palette until
  // we find a non-conflicting hue.
  const colors = [];
  const paletteSize = SCENE_PALETTE.length;
  nameToColor = new Map();
  speakerNames.forEach((name, i) => {
    // 1. User override always wins
    if (userOverrides[name]) {
      colors.push(userOverrides[name]);
      nameToColor.set(name, userOverrides[name]);
      return;
    }
    // 2. Preferred candidate (hand-picked OR round-robin)
    let candidate = SPEAKER_BACKDROP_MAP[name] || SCENE_PALETTE[i % paletteSize];
    const prev1 = colors[i - 1];
    const prev2 = colors[i - 2];
    // 3. Collision avoidance: if the candidate equals either of the
    // previous two slots, step through the palette until it doesn't.
    if (candidate === prev1 || candidate === prev2) {
      const startIdx = SCENE_PALETTE.indexOf(candidate);
      const base = startIdx >= 0 ? startIdx : (i % paletteSize);
      for (let step = 1; step < paletteSize; step += 1) {
        const trial = SCENE_PALETTE[(base + step) % paletteSize];
        if (trial !== prev1 && trial !== prev2) {
          candidate = trial;
          break;
        }
      }
    }
    colors.push(candidate);
    nameToColor.set(name, candidate);
  });

  if (typeof window !== 'undefined') {
    window.dispatchEvent(new Event(PALETTE_PRIMED_EVENT));
  }
}

/** Persist a user-chosen backdrop color for a speaker. Called from
 *  the "Position & backdrop" popover in SpeakerPage. Re-primes so the
 *  home grid picks up the choice without a page reload. */
export function setSpeakerBackdropOverride(name, hex) {
  if (!name) return;
  if (hex && typeof hex === 'string') {
    userOverrides[name] = hex;
  } else {
    delete userOverrides[name];
  }
  try {
    localStorage.setItem(USER_OVERRIDES_KEY, JSON.stringify(userOverrides));
  } catch { /* quota */ }
  // Re-run the greedy pass in-place so the change reflects everywhere.
  nameToColor.set(name, userOverrides[name] || nameToColor.get(name));
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new Event(PALETTE_PRIMED_EVENT));
  }

  // Feb-2026 permanent rule: also persist to the backend so the choice
  // survives across devices. Fire-and-forget; on failure we still have
  // localStorage as the safety net.
  try {
    const encoded = encodeURIComponent(name);
    if (hex && typeof hex === 'string') {
      fetch(`${API_BASE}/api/portrait/backdrops/${encoded}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ color: hex }),
      }).catch(() => { /* offline is ok */ });
    } else {
      fetch(`${API_BASE}/api/portrait/backdrops/${encoded}`, { method: 'DELETE' })
        .catch(() => { /* offline is ok */ });
    }
  } catch (_) { /* ignore */ }
}

export function clearSpeakerBackdropOverride(name) {
  setSpeakerBackdropOverride(name, null);
}

/** Deterministic 32-bit hash used as the fallback when a speaker is
 *  not present in the primed order (e.g. a fresh name not yet
 *  returned by `/api/speakers`). */
const nameHash = (str) => {
  let h = 0;
  const s = String(str || '');
  for (let i = 0; i < s.length; i += 1) {
    h = ((h * 31) + s.charCodeAt(i)) >>> 0;
  }
  return h;
};

export function getSpeakerPaletteIndex(name) {
  const primed = nameToIndex.get(name);
  if (typeof primed === 'number') return primed % SCENE_PALETTE.length;
  return nameHash(name) % SCENE_PALETTE.length;
}

export function getSpeakerPaletteBase(name) {
  // 1. Greedy-assigned color from `primePaletteOrder` (includes user
  //    overrides) — this is the canonical answer post-priming.
  if (nameToColor.has(name)) return nameToColor.get(name);
  // 2. Persisted user override even before priming has run.
  if (userOverrides[name]) return userOverrides[name];
  // 3. Hand-picked flattering match — falls back to round-robin.
  const matched = SPEAKER_BACKDROP_MAP[name];
  if (matched) return matched;
  return SCENE_PALETTE[getSpeakerPaletteIndex(name)];
}

export function getSpeakerPaletteScene(name) {
  return sceneCss(getSpeakerPaletteBase(name));
}
